/**
 * statisticalHelpers.ts
 *
 * Consolidated statistical helper functions to eliminate duplication
 * Used across CombinedMonteCarloEngine and PerformanceCalculationService
 *
 * OPTIMIZED: Now includes batch statistical calculations for improved performance
 * - Batch processing reduces O(n log n) × methods to single sort + O(n) calculations
 * - Backward compatible with existing code
 * - New optimized methods available for high-performance scenarios
 */

import { BatchStatistics, calculateAllStatistics } from './optimizedCalculations';

export class StatisticalHelpers {
  /**
   * Calculate arithmetic mean of an array
   */
  static calculateMean(values: number[]): number {
    if (values.length === 0) {return 0;}
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }

  /**
   * Calculate median of an array
   */
  static calculateMedian(values: number[]): number {
    if (values.length === 0) {return 0;}
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  /**
   * Calculate percentile of an array (0-100)
   */
  static calculatePercentile(values: number[], percentile: number): number {
    if (values.length === 0) {return 0;}
    const sorted = [...values].sort((a, b) => a - b);

    if (percentile <= 0) {return sorted[0];}
    if (percentile >= 100) {return sorted[sorted.length - 1];}

    const index = (percentile / 100) * (sorted.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    if (lower === upper) {return sorted[lower];}
    return sorted[lower] * (1 - weight) + sorted[upper] * weight;
  }

  /**
   * Calculate standard deviation of an array
   */
  static calculateStandardDeviation(values: number[]): number {
    if (values.length < 2) {return 0;}
    const mean = this.calculateMean(values);
    // Sample standard deviation (Bessel's correction, ÷(n-1)) - consistent with
    // calculateAllStatistics in optimizedCalculations.ts.
    const variance =
      values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);
    return Math.sqrt(variance);
  }

  /**
   * Calculate (sample) variance of an array.
   * Uses Bessel's correction (÷(n-1)), consistent with calculateStandardDeviation
   * above - i.e. calculateVariance(x) === calculateStandardDeviation(x) ** 2.
   */
  static calculateVariance(values: number[]): number {
    if (values.length < 2) {return 0;}
    const mean = this.calculateMean(values);
    return values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);
  }

  /**
   * Calculate multiple percentiles at once for efficiency
   */
  static calculateMultiplePercentiles(
    values: number[],
    percentiles: number[]
  ): Record<number, number> {
    if (values.length === 0) {
      return percentiles.reduce((acc, p) => ({ ...acc, [p]: 0 }), {});
    }

    const sorted = [...values].sort((a, b) => a - b);
    const result: Record<number, number> = {};

    percentiles.forEach(p => {
      result[p] = this.calculatePercentile(sorted, p);
    });

    return result;
  }

  /**
   * Calculate min and max values
   */
  static calculateMinMax(values: number[]): { min: number; max: number } {
    if (values.length === 0) {return { min: 0, max: 0 };}
    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }

  /**
   * Calculate coefficient of variation (relative standard deviation)
   */
  static calculateCoefficientOfVariation(values: number[]): number {
    const mean = this.calculateMean(values);
    if (mean === 0) {return 0;}
    const stdDev = this.calculateStandardDeviation(values);
    return stdDev / Math.abs(mean);
  }

  /**
   * Calculate skewness (measure of asymmetry)
   */
  static calculateSkewness(values: number[]): number {
    if (values.length < 3) {return 0;}

    const mean = this.calculateMean(values);
    const stdDev = this.calculateStandardDeviation(values);

    if (stdDev === 0) {return 0;}

    const n = values.length;
    const skewness =
      values.reduce((sum, val) => {
        return sum + Math.pow((val - mean) / stdDev, 3);
      }, 0) / n;

    return skewness;
  }

  /**
   * Calculate kurtosis (measure of tail heaviness)
   */
  static calculateKurtosis(values: number[]): number {
    if (values.length < 4) {return 0;}

    const mean = this.calculateMean(values);
    const stdDev = this.calculateStandardDeviation(values);

    if (stdDev === 0) {return 0;}

    const n = values.length;
    const kurtosis =
      values.reduce((sum, val) => {
        return sum + Math.pow((val - mean) / stdDev, 4);
      }, 0) / n;

    return kurtosis - 3; // Excess kurtosis (0 for normal distribution)
  }

  // ===== OPTIMIZED METHODS FOR HIGH PERFORMANCE =====

  /**
   * OPTIMIZED: Calculate all statistical measures in a single pass
   *
   * Performance improvement: Reduces multiple O(n log n) operations to single sort + linear calculations
   * Use this method when you need multiple statistical measures for better performance.
   *
   * @param values - Array of numerical values
   * @returns BatchStatistics with all common statistical measures
   *
   * @example
   * ```typescript
   * // Instead of calling multiple separate methods:
   * // const mean = StatisticalHelpers.calculateMean(values);
   * // const median = StatisticalHelpers.calculateMedian(values);
   * // const stdDev = StatisticalHelpers.calculateStandardDeviation(values);
   * // const p10 = StatisticalHelpers.calculatePercentile(values, 10);
   * // const p90 = StatisticalHelpers.calculatePercentile(values, 90);
   *
   * // Use optimized batch calculation:
   * const stats = StatisticalHelpers.calculateAllStatisticsOptimized(values);
   * console.log(`Mean: ${stats.mean}, Median: ${stats.median}, StdDev: ${stats.stdDev}`);
   * console.log(`Risk (10th percentile): ${stats.p10}, Upside (90th percentile): ${stats.p90}`);
   * ```
   */
  static calculateAllStatisticsOptimized(values: number[]): BatchStatistics {
    return calculateAllStatistics(values);
  }

}
